import React, { useState, useRef, useLayoutEffect, createElement } from 'react';
import {setDayorNight} from '@utils/common';

interface State {
  loading: boolean;
  loaded: any;
  error: any;
  promise: Promise<any> | null;
}

type ReactComponent =
  | string
  | React.FunctionComponent<any>
  | React.ComponentClass<any, any>;

type LoadFn = (loader: Loader) => State;

type Loader = () => Promise<any>;

type ReactFn = () => ReactComponent;

type ReactFnPromise = () => Promise<ReactComponent>;

interface Opts {
  loader: ReactFnPromise;
  loading: ReactFn;
  [x: string]: any;
}

interface State2 {
  error: any;
  loading: boolean;
  loaded: any;
}

function useCreateLoadableComponent(loadFn: LoadFn, opts: Opts) {
  let [state, setState] = useState<State2>({
    error: null,
    loading: true,
    loaded: null,
  });
  const res = useRef<State | null>(null);

  const update = () => {
    setState({
      error: res.current!.error,
      loading: res.current!.loading,
      loaded: res.current!.loaded,
    });
  };

  useLayoutEffect(() => {
    setDayorNight();
    if (!res.current) {
      res.current = loadFn(opts.loader);
    }
    
    update();

    res
      .current!.promise!.then(() => {
        update();
      })
      .catch(() => {
        update();
      });
  }, [loadFn, opts]);

  return (props: any) => {
    if (state.loading || state.error) {
      return createElement(opts.loading(), props);
    } else if (state.loaded) {
      return createElement(state.loaded, { ...opts, ...props });
    } else {
      return null;
    }
  };
}

function createLoadableComponent(loadFn: LoadFn, opts: Opts) {
  if (!opts.loading) {
    throw new Error('Requires a `loading` component');
  }

  return (props: any) => useCreateLoadableComponent(loadFn, opts)(props);
}

function load(loader: Loader): State {
  let promise = loader();

  let state: State = {
    loading: true,
    loaded: null,
    error: null,
    promise: null,
  };

  state.promise = promise
    .then((loaded) => {
      state.loading = false;
      state.loaded = loaded.default;
      return loaded;
    })
    .catch((err) => {
      state.loading = false;
      state.error = err;
      throw err;
    });

  return state;
}

function Loadable(opts: Opts) {
  return createLoadableComponent(load, opts);
}

export default Loadable;
